import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageTitle from '../ui/PageElements/PageTitle/PageTitle'
import { Alert, Divider, Empty, Form, Input, message, Tooltip } from 'antd'
import style from './style.m.less'
import { BackArrow, Box, CenteredSpin, DropdownSelector2, PrimaryButton } from '../ui'
import { DropdownSelector3 } from './components'
import { PreviewModal } from '../ui/Certificates/PreviewModal/PreviewModal'
import PageContainer from '../ui/PageElements/PageContainer/PageContainer'
import Card from '../ui/Card/Card'
import { useNavigate } from 'react-router-dom'
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { RequestTypes } from '../hooks'
import useRequest from '../hooks/useRequest'
import { useAppSelector } from '../../store/hooks'
import { ProfileShortData, ConsumptionObject, ThirdPartyTool, ThirdPartyToolOption } from './types'
import { format_date } from './helpers'

const RETURN_URL = '/ownership-certificates?tab=1'

const getResponseList = <T,>(data: any): T[] => {
    if (Array.isArray(data)) {
        return data
    }

    if (Array.isArray(data?.detail)) {
        return data.detail
    }

    return []
}

const getToolKey = (tool: ThirdPartyTool) =>
    [tool.company_name, tool.third_party_company, tool.end_date, tool.count, tool.volume].join('|')

const getToolDisplayName = (tool: ThirdPartyTool) =>
    `${tool.company_name} / ${tool.end_date} / ${tool.count} / ${tool.volume}`

export const IssueOwnershipCertificateExternal: FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const userInfo = useAppSelector((store) => store.user)
    const { data } = userInfo

    const [form] = Form.useForm()
    const [selectedUser, setSelectedUser] = useState<ProfileShortData | null>(null)
    const [displayedUser, setDisplayedUser] = useState<string>()
    const [period, setPeriod] = useState<string>()
    const [consumptionObjectInput, setConsumptionObjectInput] = useState<string>('')
    const [selectedToolKey, setSelectedToolKey] = useState<string>()

    const [users, setUsers] = useState<ProfileShortData[]>([])
    const [toolOptions, setToolOptions] = useState<ThirdPartyToolOption[]>([])
    const [consumptionObjects, setConsumptionObjects] = useState<ConsumptionObject[]>([])
    const [thirdPartyTools, setThirdPartyTools] = useState<ThirdPartyTool[]>([])

    const [disableSend, setDisableSend] = useState(true)

    const {
        fetch: fetchUsers,
        isLoading: isLoadingUsers,
        isError: IsErrorUser,
    } = useRequest({
        link: '/api/extension/v1/extension/ownership_certificates/external/users',
        method: RequestTypes.get,
    })
    const { fetch: fetchExternalAttributes } = useRequest({
        link: '/api/extension/v1/extension/ownership_certificates/external/attributes',
        method: RequestTypes.get,
    })
    const { fetch: fetchCertificateCreate } = useRequest({
        link: '/api/extension/v1/extension/ownership_certificates/external',
        method: RequestTypes.post,
    })

    useEffect(() => {
        if (data.uid) {
            loadUsers(data.uid)
        }
    }, [data])

    useEffect(() => {
        const isReady =
            !!selectedUser && consumptionObjects.length > 0 && thirdPartyTools.length > 0

        setDisableSend(!isReady)
    }, [selectedUser, consumptionObjects, thirdPartyTools])

    const resetTools = () => {
        setSelectedToolKey(undefined)
        setToolOptions([])
        setThirdPartyTools([])
        form.setFieldsValue({ third_party_tool: undefined })
    }

    const loadUsers = (externalSystemId: string) => {
        fetchUsers({
            addToLink: `?ext_user_id=${externalSystemId}`,
            onSuccess: (data) => {
                setUsers(getResponseList<ProfileShortData>(data))
            },
            onError: () => {
                setUsers([])
                message.error(t('ownershipCertificate.certForm.emptyText'))
            },
        })
    }

    const loadExternalAttributes = (userId: string) => {
        fetchExternalAttributes({
            addToLink: `?user_id=${userId}&ext_user_id=${data.uid}`,
            onSuccess: (data) => {
                const list = getResponseList<ThirdPartyTool>(data)
                setToolOptions(
                    list.map((tool) => ({
                        ...tool,
                        tool_key: getToolKey(tool),
                        display_name: getToolDisplayName(tool),
                    })),
                )
            },
            onError: () => {
                setToolOptions([])
                message.error(t('ownershipCertificate.certForm.emptyText'))
            },
        })
    }

    const handleUserChange = (value: string) => {
        const user = users.find((item) => item.user_id === value) || null
        setSelectedUser(user)
        resetTools()

        if (user) {
            loadExternalAttributes(user.user_id)
        }
    }

    const addConsumptionObject = () => {
        const name = consumptionObjectInput.trim()

        if (!name || consumptionObjects.some((item) => item.name === name)) {
            return
        }

        setConsumptionObjects((prev) => [...prev, { name }])
        setConsumptionObjectInput('')
        form.setFieldsValue({ consumption_object: '' })
    }

    const removeConsumptionObject = (index: number) => {
        setConsumptionObjects((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
    }

    const addThirdPartyTool = () => {
        const tool = toolOptions.find((item) => item.tool_key === selectedToolKey)

        if (!tool) {
            return
        }

        const exists = thirdPartyTools.some((item) => getToolKey(item) === tool.tool_key)

        if (!exists) {
            const { tool_key, display_name, ...thirdPartyTool } = tool
            setThirdPartyTools((prev) => [...prev, thirdPartyTool])
        }

        setSelectedToolKey(undefined)
        form.setFieldsValue({ third_party_tool: undefined })
    }

    const removeThirdPartyTool = (index: number) => {
        setThirdPartyTools((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
    }

    const getPayload = () => ({
        external_system: { user_id: data.uid, company_name: data.company_name_short },
        user: selectedUser,
        displayed_user: displayedUser?.trim() || null,
        period: period?.trim() || null,
        consumption_objects: consumptionObjects,
        third_party_tools: thirdPartyTools,
    })

    const onFinish = async () => {
        if (!selectedUser) {
            return
        }

        if (!consumptionObjects.length) {
            message.error(t('ownershipCertificate.certForm.objectPlaceholder'))
            return
        }

        if (!thirdPartyTools.length) {
            message.error(t('ownershipCertificate.certForm.greenToolPlaceholder'))
            return
        }

        setDisableSend(true)
        fetchCertificateCreate({
            body: getPayload(),
            showError: false,
            onSuccess: () => navigate(RETURN_URL),
            onError: (err) => {
                message.error(err?.detail || t('ownershipCertificate.certForm.emptyText'))
                setDisableSend(false)
            },
        })
    }

    if (isLoadingUsers) {
        return <CenteredSpin />
    }

    return (
        <PageContainer className={style.container}>
            <PageTitle
                title={
                    <>
                        <BackArrow link={RETURN_URL} />
                        {t('ownershipCertificate.certTable.certificateExternalButton')}
                    </>
                }
            />
            {!IsErrorUser ? (
                <Card>
                    <Form
                        form={form}
                        onFinish={onFinish}
                        name="issueOwnershipCertificateExternal"
                        layout="vertical"
                    >
                        <Form.Item
                            name="user_id"
                            className={style.dropdownWrapper}
                            label={
                                <>
                                    {t('ownershipCertificate.certForm.userLabel')}
                                    <Tooltip
                                        title="Владелец атрибутов генерации, указанный при передаче атрибутов генерации по стороннему инструменту"
                                        overlayStyle={{ maxWidth: 300 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </>
                            }
                            rules={[
                                {
                                    required: true,
                                    message: t('ownershipCertificate.certForm.userRequired'),
                                },
                            ]}
                        >
                            <DropdownSelector2
                                placeholder={t('ownershipCertificate.certForm.userPlaceholder')}
                                size="large"
                                options={users.map((item) => ({
                                    text: item.company_name,
                                    label: item.company_name,
                                    value: item.user_id,
                                    id: item.user_id,
                                }))}
                                onChange={handleUserChange}
                                className={style.dropdownUser}
                                customStyle={{ width: '400px' }}
                                noDefaultValue={true}
                            />
                        </Form.Item>

                        <Form.Item
                            name="displayed_user"
                            label={
                                <>
                                    {t('ownershipCertificate.certForm.newNameLabel')}
                                    <Tooltip
                                        title='Допустимо уточнить наименование владельца атрибутов генерации (указать/убрать кавычки, строчные/заглавные буквы, иные уточнения). Указанная в данном поле информация, в случае ее заполнения, будет отображаться в Свидетельстве. Если данное поле останется незаполненным, то в Свидетельстве будет отображаться информация, указанная в поле "Владелец атрибутов генерации".'
                                        overlayStyle={{ maxWidth: 400 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </>
                            }
                            className={style.dropdownWrapper}
                        >
                            <Input
                                placeholder={t('ownershipCertificate.certForm.newNamePlaceholder')}
                                className={style.input}
                                value={displayedUser}
                                onChange={(e) => setDisplayedUser(e.target.value)}
                            />
                        </Form.Item>

                        <Divider />

                        <Form.Item
                            name="period"
                            label={
                                <>
                                    {t('ownershipCertificate.certForm.periodLabel')}
                                    <Tooltip
                                        title="При заполнении данного поля указанная информация будет отображаться внизу Свидетельства, после перечисления объектов потребления электроэнергии"
                                        overlayStyle={{ maxWidth: 400 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </>
                            }
                            className={style.dropdownWrapper}
                        >
                            <Input
                                placeholder={t('ownershipCertificate.certForm.periodPlaceholder')}
                                className={style.input}
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                            />
                        </Form.Item>

                        <Form.Item
                            name="consumption_object"
                            label={
                                <>
                                    <div className={style.star}>*</div>
                                    {t('ownershipCertificate.certForm.objectsLabel')}
                                    <Tooltip
                                        title='Поле для указания одного или нескольких объектов/мероприятий/процессов/событий в отношении которых возникли права на совершение действий в связи с владением атрибутами генерации. Например: "Проведения мероприятия….", "Завода… по адресу:…" и т.п. (рекомендуется указывать информацию в родительном падеже). Допустимо указать несколько объектов в одну строку или добавлять каждый новый объект с помощью кнопки "Добавить объект" (в этом случае объекты будут отображаться с новой строки или разделяться символом "/" при отображении в Свидетельстве, если таких объектов больше шести). Вся введенная информация будет отображаться после строки "Указанный объем атрибутов генерации соответствует количеству электрической энергии, потребленной (купленной в отношении)*:"'
                                        overlayStyle={{ maxWidth: 500 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </>
                            }
                            className={style.dropdownWrapper}
                        >
                            <Input
                                placeholder={t('ownershipCertificate.certForm.objectPlaceholder')}
                                className={style.input}
                                value={consumptionObjectInput}
                                onChange={(e) => setConsumptionObjectInput(e.target.value)}
                                onPressEnter={(e) => {
                                    e.preventDefault()
                                    addConsumptionObject()
                                }}
                            />
                        </Form.Item>

                        <button
                            className={style.primaryButton}
                            type="button"
                            onClick={addConsumptionObject}
                            disabled={!consumptionObjectInput.trim()}
                        >
                            {t('ownershipCertificate.certForm.addObjectButton')}
                        </button>

                        <div className={style.agentsGrid}>
                            {consumptionObjects.map((item, index) => (
                                <Alert
                                    key={`${item.name}-${index}`}
                                    className={style.agentAlert}
                                    message={
                                        <div className={style.alertTitle}>{`${t(
                                            'ownershipCertificate.certForm.objectText',
                                        )}: ${item.name}`}</div>
                                    }
                                    description={
                                        <CloseOutlined
                                            className={style.alertCloseIcon}
                                            onClick={() => removeConsumptionObject(index)}
                                        />
                                    }
                                    type="info"
                                />
                            ))}
                        </div>

                        <Divider />

                        <Form.Item
                            name="third_party_tool"
                            label={
                                <>
                                    <div className={style.star}>*</div>
                                    Сторонние инструменты
                                    <Tooltip
                                        title='Необходимо указать один сторонний инструмент (группу инструментов), который был использован для передачи атрибутов генерации владельцу, выбранному в поле "Владелец атрибутов генерации" с учетом даты передачи атрибутов генерации.'
                                        overlayStyle={{ maxWidth: 400 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </>
                            }
                            className={style.dropdownWrapper}
                        >
                            <DropdownSelector3
                                placeholder="Выберите сторонний инструмент"
                                size="large"
                                options={toolOptions
                                    .filter(
                                        (toolOption) =>
                                            !thirdPartyTools.some(
                                                (t) => getToolKey(t) === toolOption.tool_key,
                                            ),
                                    )
                                    .map((item) => ({
                                        text: item.display_name,
                                        label: item.display_name,
                                        value: item.tool_key,
                                        id: item.tool_key,
                                    }))}
                                onChange={(value) => setSelectedToolKey(value)}
                                value={selectedToolKey}
                                disabled={!selectedUser || thirdPartyTools.length > 0}
                            />
                        </Form.Item>

                        <button
                            className={style.primaryButton}
                            type="button"
                            disabled={!selectedToolKey}
                            onClick={addThirdPartyTool}
                        >
                            Добавить сторонний инструмент
                        </button>

                        <div className={style.agentsGrid}>
                            {thirdPartyTools.map((tool, index) => (
                                <Alert
                                    key={`${tool.company_name}-${tool.third_party_company}-${tool.end_date}-${index}`}
                                    className={style.agentAlert}
                                    style={{ width: 'max-content' }}
                                    message={
                                        <div className={style.alertTitle}>
                                            Объем: {tool.volume} кВт·ч
                                        </div>
                                    }
                                    description={
                                        <>
                                            <CloseOutlined
                                                className={style.alertCloseIcon}
                                                onClick={() => removeThirdPartyTool(index)}
                                            />
                                            <div className={style.alertDetail}>
                                                <span style={{ whiteSpace: 'pre-wrap' }}>
                                                    Номер:{' '}
                                                    {tool?.numbers?.map((number, index) => {
                                                        if (
                                                            tool?.numbers &&
                                                            index < tool?.numbers?.length - 1
                                                        ) {
                                                            return number + ',\n'
                                                        } else {
                                                            return number
                                                        }
                                                    })}
                                                </span>
                                                <span>
                                                    Дата погашения: {format_date(tool.end_date)}
                                                </span>
                                                <span>Количество: {tool.count}</span>
                                            </div>
                                        </>
                                    }
                                    type="info"
                                />
                            ))}
                        </div>
                    </Form>
                    <Alert
                        className={style.modalAlert}
                        message={
                            <div className={style.modalAlertText}>
                                <div>{t('ownershipCertificate.certForm.tooltips.submitFirst')}</div>
                                <div>
                                    {t('ownershipCertificate.certForm.tooltips.submitSecond')}
                                </div>
                            </div>
                        }
                        type="warning"
                        showIcon
                    />
                    <Divider />
                    <Box direction="row" justify="flex-start" margin="32px 0px 0px 0px">
                        <PrimaryButton
                            onClick={() => navigate(RETURN_URL)}
                            value={t('ownershipCertificate.certForm.cancelButton')}
                            isCancel
                            customStyle={{ padding: '8px 16px' }}
                        />
                        <PrimaryButton
                            onClick={() => form.submit()}
                            disabled={disableSend}
                            value={t('ownershipCertificate.certForm.saveButton')}
                            customStyle={{ marginLeft: '16px', padding: '8px 16px' }}
                        />
                        <PreviewModal
                            previewUrl="/api/extension/v1/files/certificates/ownership/external/preview"
                            setDisableSend={setDisableSend}
                            getPayload={getPayload}
                            disabled={disableSend}
                        />
                    </Box>
                </Card>
            ) : (
                <Card className={style.emptyCard}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('ownershipCertificate.certForm.emptyText')}
                    />
                </Card>
            )}
        </PageContainer>
    )
}

export default IssueOwnershipCertificateExternal
